import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  FormHelperText, 
  Grid, 
  MenuItem, 
  TextField as MuiTextField,
  Drawer, useMediaQuery, Stack } from "@mui/material";
import { push } from "connected-react-router";
import { useFormik } from "formik";
import { useCreate, useGetMainList } from "ra-core";
import { cloneElement, FC, ReactElement, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import * as Yup from 'yup'
import { useHasPermit } from "~/authProvider/useHasPermit";
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import { SCHEDULE_ENCRYPTION_OBFUSCATION } from "~/constants";
import { RecPatch } from "~/dataProvider/tools";
import { BackupResponseDto } from "~/gen/api/backup";
import { ObfuscationMappingResponseDto, ScheduleEncryptionInput } from "~/gen/api/encrypt";
import { EncryptionMappingResponseDto } from "~/gen/api/governance";
import { genUrlWithHashid } from "~/utils/hashid";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import SelectField from "../formik/SelectField";

const validationSchema =
  Yup.object().shape({
    database: Yup.object().required('Database is required'),
    backupTimestamp: Yup.object().required('Backup Timestamp is required'),
  })

const initialValues = {
  submit: '',
  database: '' as EncryptionMappingResponseDto | '',
  backupTimestamp: '' as BackupResponseDto | '',
}

const ScheduleEncryption: FC<{ button?: ReactElement, isObfuscation?: boolean }> = ({ button, isObfuscation }) => {
  const hasWritePermit = useHasPermit([SCHEDULE_ENCRYPTION_OBFUSCATION])
  const [ save ] = useCreate<ScheduleEncryptionInput & RecPatch>('encryption/list')
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const location = useLocation()

  useEffect(() => {
    if (location.state && typeof location.state === 'object') {
      const { openCreate }: {openCreate:boolean} = location.state as any
      if (openCreate) {
        setOpen(true)
        dispatch(push(location.pathname, null))
      }
    }
  }, [location, dispatch])

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
    resetForm,
  }) => {
    if (!hasWritePermit || values.database === '' || values.backupTimestamp === '') return;
    const db = values.database
    const input: ScheduleEncryptionInput = {
      databaseId: values.database.databaseId,
      backupId: values.backupTimestamp.backupId,
      isObfuscation: !!isObfuscation,
    }

    const options = {
      onSuccess: () => {
        resetForm({
          status: { success: true },
          values: initialValues,
          isSubmitting: false,
        })
        setOpen(false)
        dispatch(push(
          genUrlWithHashid(
            isObfuscation ? '/encryption/obfs/list/$0/show' : `/encryption/list/$0/show`, 
            input.databaseId || 0, 
            db.databaseName
          )))
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }
    save('encryption/list', input, options)
  }, [save, hasWritePermit])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, values, resetForm } = formikCtrl

  const handleOpen = useCallback((): void => {
    setOpen(true)
  }, [])
  const handleClose = useCallback((): void => {
    setOpen(false);
    resetForm({
      status: { success: true },
      values: initialValues,
      isSubmitting: false,
    })
  }, [resetForm]);

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const { data: encryptListBag } = useGetMainList<EncryptionMappingResponseDto & RecPatch>(
    isObfuscation ? 'encryption/obfs/mappings' : 'encryption/mappings', pages(), sorts(), {})
  const encryptList = Object.values(encryptListBag || {})
    .filter(x => (!isObfuscation && ((x.mappedCount ?? 0) > 0 || (x.skippedCount ?? 0) > 0)) || ((x as ObfuscationMappingResponseDto)?.obfsMappedCount || 0) > 0)
    .sort((a, b) => (a.databaseName || '') < (b.databaseName || '') ? -1 : 1)

  const { data: encryptableBackupsBag } = useGetMainList<BackupResponseDto & RecPatch>(
    values.database !== '' ? 'encryptable-backups' : 'noop', pages(), sorts(), {
    databaseId: values.database !== '' ? values.database.databaseId : -1,
  })
  const encryptableBackups = Object.values(encryptableBackupsBag || {}).sort((a, b) => b.id - a.id)

  const format = useUserTimeFormat()

  const triggerButton = button || <Button variant="contained">Schedule {isObfuscation ? 'Obfuscation' : 'Encryption'} Job</Button>
  return (
    <>
      {cloneElement(triggerButton, { onClick: handleOpen })}
      {hasWritePermit &&
        <Drawer
          anchor="right"
          onClose={handleClose}
          open={open}
          PaperProps={{
            sx: {
              p: 2,
              width: width,
            }
          }}
        >
          <OdForm formikCtrl={formikCtrl}>

            <Card>
              <CardHeader title={isObfuscation ? 'Schedule Obfuscation' : "Schedule Encryption"} />
              <CardContent>
                <Grid
                  container
                  spacing={4}
                >
                  <Grid item xs={12} >
                    <SelectField 
                      label="Database*" 
                      name="database" 
                    >
                      {encryptList.map(d => (
                        <MenuItem key={d.databaseId} value={d as any} >{d.databaseName}</MenuItem>
                      ))}
                    </SelectField>
                  </Grid>
                  <Grid item xs={12} >
                    <SelectField 
                      label="Backup Timestamp*" 
                      name="backupTimestamp" 
                    >
                      {encryptableBackups.map(d => (
                        <MenuItem key={d.backupId} value={d as any} >
                          {format(d.createdTime || 0, 'Pp')}
                        </MenuItem>
                      ))}
                    </SelectField>
                  </Grid>
                  <Grid item xs={12} >
                    <MuiTextField 
                      id="engine"
                      label="Engine" 
                      name="engine" 
                      fullWidth
                      inputProps={{
                        readOnly: true,
                      }}
                      value={values.database !== '' 
                        ? (`${values.database.engineVersion || ''} ${values.database.engineEdition || ''}`) 
                        : ''} 
                      onChange={()=>{}}
                    />
                  </Grid>
                </Grid>
                {errors.submit && (
                  <Box sx={{ mt: 3 }}>
                    <FormHelperText error>
                      {errors.submit}
                    </FormHelperText>
                  </Box>
                )}
              </CardContent>
              <Stack
                direction="row"
                spacing={6}
                sx={{
                  justifyContent: 'space-between',
                  p: 2
                }}
              >
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  variant="outlined"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  type="submit"
                  variant="contained"
                >
                  Add Schedule
                </Button>
              </Stack>
            </Card>
          </OdForm>
        </Drawer>}
    </>
  )
}

export default ScheduleEncryption;
